import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { pricingPlans } from '../data/mock';

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const factor = annual ? 0.8 : 1; // 20% off annual
  return (
    <div className="bg-white">
      <Header />
      <section className="bg-gradient-to-b from-slate-50 to-white pt-16 pb-10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[#8BC53F] font-bold tracking-widest text-sm mb-3">PRICING</p>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900">Simple, Transparent Pricing</h1>
          <p className="mt-6 text-slate-600 text-xl">Choose the plan that fits your business. Cancel anytime, no hidden fees.</p>
          <div className="mt-8 inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-5 py-2 shadow-sm">
            <span className={`text-sm font-medium ${!annual ? 'text-slate-900' : 'text-slate-500'}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <span className={`text-sm font-medium ${annual ? 'text-slate-900' : 'text-slate-500'}`}>Annual</span>
            <span className="text-xs font-bold text-[#6FA82C] bg-[#6FA82C]/10 px-2 py-0.5 rounded-full">SAVE 20%</span>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {pricingPlans.map(p => {
            const numericMatch = p.price.match(/\$(\d+)/);
            const numeric = numericMatch ? Math.round(parseInt(numericMatch[1]) * factor) : null;
            return (
              <div key={p.name} className={`relative pp-lift bg-white rounded-2xl border ${p.popular ? 'border-[#8BC53F] shadow-2xl scale-[1.02]' : 'border-slate-200 shadow-sm'} p-8 flex flex-col`}>
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#8BC53F] to-[#6FA82C] text-white text-xs font-bold tracking-wider px-4 py-1.5 rounded-full flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5"/>MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-extrabold text-slate-900">{p.name}</h3>
                <p className="text-slate-600 text-sm mt-1">{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold text-slate-900">{numeric ? `$${numeric}` : p.price}</span>
                  <span className="text-slate-500">{p.period}</span>
                </div>
                <Button asChild className={`mt-6 h-12 font-bold ${p.popular ? 'bg-[#8BC53F] hover:bg-[#6FA82C] text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
                  <Link to="/login">{p.cta}</Link>
                </Button>
                <ul className="mt-8 space-y-3 flex-1">
                  {p.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-5 h-5 text-[#6FA82C] flex-shrink-0 mt-0.5"/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 text-center mb-8">Frequently Asked Questions</h2>
          {[
            {q:'Can I switch plans later?',a:'Absolutely. You can upgrade or downgrade your plan at any time. Changes take effect on your next billing cycle.'},
            {q:'Do you offer a free trial?',a:'Yes — every plan starts with a no-commitment demo and a 14-day trial of the full Professional feature set.'},
            {q:'Is implementation included?',a:'Professional and Enterprise plans include guided onboarding, data migration, and live training from our success team.'},
            {q:'What integrations are available?',a:'Xpertpests integrates with Stripe, Twilio, Google Maps, Resend, and more — with CSV and Excel import/export for everything else.'},
          ].map((f, i) => (
            <details key={i} className="group bg-white rounded-xl border border-slate-200 mb-3 overflow-hidden">
              <summary className="cursor-pointer p-5 font-semibold text-slate-900 list-none flex items-center justify-between">
                {f.q}
                <ArrowRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform"/>
              </summary>
              <div className="px-5 pb-5 text-slate-600 leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
